import React, { useEffect, useState } from 'react';
import { LockIcon, ShieldIcon, BellIcon, EyeIcon, EyeOffIcon, AlertTriangleIcon, LogOutIcon, CheckCircleIcon, XIcon, Loader2Icon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
type LoginSession = {
  id: string;
  device: string;
  ipAddress: string;
  location: string;
  date: string;
  isCurrent: boolean;
};
export const SecuritySection = () => {
  const {
    user,
    auth
  } = useAuth();
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // Two-factor authentication state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isSaving2FA, setIsSaving2FA] = useState(false);
  // Login sessions mock data - will be replaced with real data
  const [loginSessions, setLoginSessions] = useState<LoginSession[]>([{
    id: '1',
    device: 'Chrome on macOS',
    ipAddress: '192.168.0.1',
    location: 'Los Angeles, USA',
    date: 'Aug 3, 2023 – 4:21 PM',
    isCurrent: true
  }, {
    id: '2',
    device: 'Safari on iPhone',
    ipAddress: '192.168.0.2',
    location: 'San Francisco, USA',
    date: 'Aug 1, 2023 – 9:45 AM',
    isCurrent: false
  }, {
    id: '3',
    device: 'Firefox on Windows',
    ipAddress: '192.168.0.3',
    location: 'New York, USA',
    date: 'Jul 28, 2023 – 2:30 PM',
    isCurrent: false
  }]);
  // Security alerts state
  const [securityAlerts, setSecurityAlerts] = useState({
    suspiciousLogin: true,
    passwordChanged: true
  });
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingAlerts, setIsSavingAlerts] = useState(false);
  const [alertsError, setAlertsError] = useState('');
  const [alertsSuccess, setAlertsSuccess] = useState(false);
  // Session management state
  const [isSigningOut, setIsSigningOut] = useState(false);
  // Fetch user security settings
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      try {
        const securityDocRef = doc(db, 'userSecurity', user.id);
        const securityDoc = await getDoc(securityDocRef);
        if (securityDoc.exists()) {
          const data = securityDoc.data();
          // Initialize security settings from Firestore
          if (data.twoFactorEnabled !== undefined) {
            setTwoFactorEnabled(data.twoFactorEnabled);
          }
          if (data.securityAlerts) {
            setSecurityAlerts(data.securityAlerts);
          }
          // We would fetch real login sessions here in a complete implementation
          // For now we'll keep the mock data
        } else {
          // Create a new security document with default settings if it doesn't exist
          const defaultSettings = {
            twoFactorEnabled: false,
            securityAlerts: {
              suspiciousLogin: true,
              passwordChanged: true
            },
            createdAt: new Date(),
            userId: user.id
          };
          await setDoc(securityDocRef, defaultSettings);
        }
      } catch (err) {
        console.error('Error fetching security settings:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSecuritySettings();
  }, [user]);
  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) {
      setPasswordError('You must be logged in to change your password');
      return;
    }
    setPasswordError('');
    setPasswordSuccess(false);
    setIsChangingPassword(true);
    // Validate password
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      setIsChangingPassword(false);
      return;
    }
    if (!/(?=.*[0-9])/.test(newPassword)) {
      setPasswordError('Password must contain at least one number');
      setIsChangingPassword(false);
      return;
    }
    if (!/(?=.*[!@#$%^&*])/.test(newPassword)) {
      setPasswordError('Password must contain at least one special character');
      setIsChangingPassword(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      setIsChangingPassword(false);
      return;
    }
    try {
      // Reauthenticate the user first
      const credential = EmailAuthProvider.credential(auth.currentUser.email || '', currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      // Then update the password
      await updatePassword(auth.currentUser, newPassword);
      // Log the password change in the security document
      if (user?.id) {
        const securityDocRef = doc(db, 'userSecurity', user.id);
        const securityDoc = await getDoc(securityDocRef);
        if (securityDoc.exists()) {
          await updateDoc(securityDocRef, {
            lastPasswordChange: new Date(),
            updatedAt: new Date()
          });
        }
        // Add an entry to the security log
        const securityLogRef = doc(db, 'securityLogs', `password-change-${Date.now()}`);
        await setDoc(securityLogRef, {
          userId: user.id,
          action: 'password-change',
          timestamp: new Date(),
          ipAddress: '127.0.0.1',
          userAgent: navigator.userAgent
        });
      }
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/too-many-requests') {
        setPasswordError('Too many attempts. Please try again later');
      } else if (error.code === 'auth/requires-recent-login') {
        setPasswordError('Please log out and log back in before changing your password');
      } else {
        setPasswordError(`Failed to change password: ${error.message}`);
      }
    } finally {
      setIsChangingPassword(false);
    }
  };
  // Handle two-factor authentication toggle
  const handleTwoFactorToggle = async () => {
    if (!user?.id) return;
    setIsSaving2FA(true);
    try {
      const newValue = !twoFactorEnabled;
      setTwoFactorEnabled(newValue);
      const securityDocRef = doc(db, 'userSecurity', user.id);
      await updateDoc(securityDocRef, {
        twoFactorEnabled: newValue,
        updatedAt: new Date()
      });
      // In a real implementation, you would set up or disable 2FA here
      // This might involve sending verification codes, etc.
    } catch (error) {
      console.error('Error updating 2FA settings:', error);
      // Revert the UI state if the update fails
      setTwoFactorEnabled(!twoFactorEnabled);
    } finally {
      setIsSaving2FA(false);
    }
  };
  // Handle security alert toggle
  const handleSecurityAlertToggle = async (alert: 'suspiciousLogin' | 'passwordChanged') => {
    if (!user?.id) return;
    setIsSavingAlerts(true);
    setAlertsError('');
    setAlertsSuccess(false);
    try {
      const newAlerts = {
        ...securityAlerts,
        [alert]: !securityAlerts[alert]
      };
      setSecurityAlerts(newAlerts);
      const securityDocRef = doc(db, 'userSecurity', user.id);
      await updateDoc(securityDocRef, {
        securityAlerts: newAlerts,
        updatedAt: new Date()
      });
      setAlertsSuccess(true);
    } catch (error) {
      console.error('Error updating security alert settings:', error);
      setAlertsError('Failed to update alert settings. Please try again.');
      // Revert the UI state if the update fails
      setSecurityAlerts({
        ...securityAlerts,
        [alert]: !securityAlerts[alert]
      });
    } finally {
      setIsSavingAlerts(false);
    }
  };
  // Handle sign out all devices
  const handleSignOutAllDevices = async () => {
    if (!user?.id || !auth.currentUser) return;
    setIsSigningOut(true);
    try {
      // In a real implementation, you would revoke all sessions except the current one
      // Firebase doesn't have a direct method for this, but you could use custom tokens
      // or maintain a list of valid sessions in Firestore
      // For now, we'll just sign out the current device
      await signOut(auth);
      // Log the action
      const securityLogRef = doc(db, 'securityLogs', `signout-all-${Date.now()}`);
      await setDoc(securityLogRef, {
        userId: user.id,
        action: 'signout-all-devices',
        timestamp: new Date(),
        ipAddress: '127.0.0.1',
        userAgent: navigator.userAgent
      });
    } catch (error) {
      console.error('Error signing out all devices:', error);
      alert('Failed to sign out all devices. Please try again.');
    } finally {
      setIsSigningOut(false);
    }
  };
  // Handle sign out from a specific session
  const handleSignOutSession = async (sessionId: string) => {
    if (!user?.id) return;
    // In a real implementation, you would revoke the specific session
    // For now, we'll just remove it from the UI
    setLoginSessions(loginSessions.filter(session => session.id !== sessionId));
    // Log the action
    try {
      const securityLogRef = doc(db, 'securityLogs', `signout-session-${Date.now()}`);
      await setDoc(securityLogRef, {
        userId: user.id,
        action: 'signout-session',
        sessionId,
        timestamp: new Date(),
        ipAddress: '127.0.0.1',
        userAgent: navigator.userAgent
      });
    } catch (error) {
      console.error('Error logging session signout:', error);
    }
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3841">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3842" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3843">
          Loading security settings...
        </span>
      </div>;
  }
  return <div data-id="element-3844">
      <div className="flex justify-between items-center mb-6" data-id="element-3845">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3846">
          Security & Login
        </h2>
      </div>
      <div className="space-y-8" data-id="element-3847">
        {/* Change Password Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3848">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3849">
            <div className="flex items-center" data-id="element-3850">
              <LockIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3851" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3852">
                Change Password
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3853">
            {passwordSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3854">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3855" />
                <p data-id="element-3856">Your password has been successfully updated.</p>
              </div>}
            {passwordError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start" data-id="element-3857">
                <AlertTriangleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3858" />
                <p data-id="element-3859">{passwordError}</p>
              </div>}
            <form onSubmit={handlePasswordChange} data-id="element-3860">
              <div className="space-y-4" data-id="element-3861">
                <div data-id="element-3862">
                  <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3863">
                    Current Password
                  </label>
                  <div className="relative" data-id="element-3864">
                    <input id="current-password" type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3865" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowCurrentPassword(!showCurrentPassword)} data-id="element-3866">
                      {showCurrentPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3867" /> : <EyeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3868" />}
                    </button>
                  </div>
                </div>
                <div data-id="element-3869">
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3870">
                    New Password
                  </label>
                  <div className="relative" data-id="element-3871">
                    <input id="new-password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3872" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowNewPassword(!showNewPassword)} data-id="element-3873">
                      {showNewPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3874" /> : <EyeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3875" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3876">
                    Password must be at least 8 characters and include a number
                    and special character.
                  </p>
                </div>
                <div data-id="element-3877">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3878">
                    Confirm New Password
                  </label>
                  <div className="relative" data-id="element-3879">
                    <input id="confirm-password" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3880" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-3881">
                      {showConfirmPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3882" /> : <EyeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3883" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-3884">
                  <button type="submit" className="px-4 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" disabled={isChangingPassword} data-id="element-3885">
                    {isChangingPassword ? <>
                        <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3886" />
                        Saving...
                      </> : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>

        {/* Two-Factor Authentication Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3887">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3888">
            <div className="flex items-center" data-id="element-3889">
              <ShieldIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3890" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3891">
                Two-Factor Authentication (2FA)
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3892">
            <div className="flex items-center justify-between mb-4" data-id="element-3893">
              <div data-id="element-3894">
                <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3895">
                  Enable Two-Factor Authentication
                </h4>
                <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3896">
                  Add an extra layer of security to your account
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-3897">
                <input type="checkbox" className="sr-only peer" checked={twoFactorEnabled} onChange={handleTwoFactorToggle} disabled={isSaving2FA} data-id="element-3898" />
                <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3899"></div>
                {isSaving2FA && <Loader2Icon className="ml-2 h-4 w-4 text-gray-500 dark:text-[#7A8F8A] animate-spin" data-id="element-3900" />}
              </label>
            </div>
            {twoFactorEnabled && <div className="bg-[#2DB489]/10 dark:bg-[#4ED1A0]/10 border border-[#2DB489]/20 dark:border-[#4ED1A0]/20 rounded-md p-4 mt-4" data-id="element-3901">
                <p className="text-sm text-gray-800 dark:text-[#F8FAF9]" data-id="element-3902">
                  2FA is currently enabled. We'll notify you when you sign in
                  from a new device.
                </p>
              </div>}
          </div>
        </section>

        {/* Connected Devices / Login History Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3903">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3904">
            <div className="flex items-center" data-id="element-3905">
              <div className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3906" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3907">
                Connected Devices
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3908">
            <div className="space-y-4" data-id="element-3909">
              {loginSessions.map(session => <div key={session.id} className={`p-4 rounded-md ${session.isCurrent ? 'bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5 border border-[#2DB489]/10 dark:border-[#4ED1A0]/10' : 'bg-gray-50 dark:bg-[#1A3330]/30 border border-gray-100 dark:border-[#1A3330]'}`} data-id="element-3910">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-id="element-3911">
                    <div data-id="element-3912">
                      <div className="flex items-center" data-id="element-3913">
                        <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3914">
                          {session.device}
                        </h4>
                        {session.isCurrent && <span className="ml-2 px-2 py-0.5 bg-[#2DB489]/10 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0] text-xs rounded-full" data-id="element-3915">
                            Current
                          </span>}
                      </div>
                      <div className="mt-1 text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3916">
                        <p data-id="element-3917">IP: {session.ipAddress}</p>
                        <p data-id="element-3918">Location: {session.location}</p>
                        <p data-id="element-3919">Last active: {session.date}</p>
                      </div>
                    </div>
                    {!session.isCurrent && <button className="text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors" onClick={() => handleSignOutSession(session.id)} data-id="element-3920">
                        Sign out
                      </button>}
                  </div>
                </div>)}
            </div>
            <div className="mt-6" data-id="element-3921">
              <button onClick={handleSignOutAllDevices} className="flex items-center px-4 py-2 border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" disabled={isSigningOut} data-id="element-3922">
                {isSigningOut ? <>
                    <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3923" />
                    Signing out...
                  </> : <>
                    <LogOutIcon className="w-4 h-4 mr-2" data-id="element-3924" />
                    Sign out of all devices
                  </>}
              </button>
            </div>
          </div>
        </section>

        {/* Security Alerts Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3925">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3926">
            <div className="flex items-center" data-id="element-3927">
              <BellIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3928" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3929">
                Security Alerts
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3930">
            {alertsSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3931">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3932" />
                <p data-id="element-3933">Your alert preferences have been updated.</p>
              </div>}
            {alertsError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start" data-id="element-3934">
                <AlertTriangleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3935" />
                <p data-id="element-3936">{alertsError}</p>
              </div>}
            <div className="space-y-4" data-id="element-3937">
              <div className="flex items-center justify-between" data-id="element-3938">
                <div data-id="element-3939">
                  <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3940">
                    Suspicious login activity
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3941">
                    Get notified when someone logs in from an unrecognized
                    device or location
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-3942">
                  <input type="checkbox" className="sr-only peer" checked={securityAlerts.suspiciousLogin} onChange={() => handleSecurityAlertToggle('suspiciousLogin')} disabled={isSavingAlerts} data-id="element-3943" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3944"></div>
                  {isSavingAlerts && <Loader2Icon className="ml-2 h-4 w-4 text-gray-500 dark:text-[#7A8F8A] animate-spin" data-id="element-3945" />}
                </label>
              </div>
              <div className="flex items-center justify-between" data-id="element-3946">
                <div data-id="element-3947">
                  <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3948">
                    Password changes
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3949">
                    Get notified when your password is changed
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-3950">
                  <input type="checkbox" className="sr-only peer" checked={securityAlerts.passwordChanged} onChange={() => handleSecurityAlertToggle('passwordChanged')} disabled={isSavingAlerts} data-id="element-3951" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3952"></div>
                </label>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>;
};